import type {
  GetPosTerminalPayment,
  GetPosTerminals,
  PostPosTerminalPayment,
  PostPosTerminalPaymentCancel,
  PostPosTapToPayResult,
  PostPosTapToPaySession,
  DeletePaymentTerminal,
  DeletePaymentTerminalConnection,
  GetPaymentTerminal,
  GetPaymentTerminalPayment,
  GetPaymentTerminals,
  PostPaymentTerminalPair,
  PostPaymentTerminalPaymentCancel,
  PostPaymentTerminalSumUpOAuthComplete,
  PostPaymentTerminalSumUpOAuthStart,
  PostPaymentTerminalTapToPayDevice,
  PostPaymentTerminalTapToPayResult,
  PostPaymentTerminalTapToPaySession,
  PostPaymentTerminalTestPayment,
  PutPaymentTerminal,
  PutPaymentTerminalSumUpMerchant,
} from "@/server-types";
import type {
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { queryClient } from "..";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { del, get, post, put } from "../request-helpers";

type QueryOpts<TData> = Omit<
  UseQueryOptions<TData, AxiosError, TData>,
  "queryKey" | "queryFn"
> & { displayNotification?: boolean };

type MutationOpts<TData, TVariables> = Omit<
  UseMutationOptions<TData, AxiosError, TVariables>,
  "mutationFn" | "onError"
> & {
  displayNotification?: boolean;
  onError?: (
    error: AxiosError,
    variables: TVariables,
    context: unknown,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
};

type PairTerminalVariables = {
  paymentTerminalConnectionId: number;
} & PostPaymentTerminalPair["body"];

type UpdateTerminalVariables = {
  paymentTerminalId: number;
} & PutPaymentTerminal["body"];

type TestPaymentVariables = {
  paymentTerminalId: number;
} & PostPaymentTerminalTestPayment["body"];

type TapToPayResultVariables = {
  terminalPaymentId: string;
} & PostPaymentTerminalTapToPayResult["body"];

const OVERVIEW_KEY = ["payment-terminals"];

export function invalidateOverview() {
  queryClient.invalidateQueries({ queryKey: OVERVIEW_KEY });
}

export const useGetOverview = (
  options?: QueryOpts<GetPaymentTerminals["response"]>,
) => {
  return useCustomQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => get<GetPaymentTerminals>("/payment-terminal"),
    ...options,
  });
};

export const usePostSumUpOAuthStart = (
  options?: MutationOpts<PostPaymentTerminalSumUpOAuthStart["response"], void>,
) => {
  return useCustomMutation({
    mutationFn: () =>
      post<PostPaymentTerminalSumUpOAuthStart>(
        "/payment-terminal/sumup/oauth/start",
      ),
    ...options,
  });
};

export const usePostSumUpOAuthComplete = (
  options?: MutationOpts<
    PostPaymentTerminalSumUpOAuthComplete["response"],
    PostPaymentTerminalSumUpOAuthComplete["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PostPaymentTerminalSumUpOAuthComplete["body"]) =>
      post<PostPaymentTerminalSumUpOAuthComplete>(
        "/payment-terminal/sumup/oauth/complete",
        body,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateOverview();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const usePutSumUpMerchant = (
  options?: MutationOpts<
    PutPaymentTerminalSumUpMerchant["response"],
    PutPaymentTerminalSumUpMerchant["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PutPaymentTerminalSumUpMerchant["body"]) =>
      put<PutPaymentTerminalSumUpMerchant>(
        "/payment-terminal/sumup/merchant",
        body,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateOverview();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useDeleteConnection = (
  options?: MutationOpts<DeletePaymentTerminalConnection["response"], number>,
) => {
  return useCustomMutation({
    mutationFn: (paymentTerminalConnectionId: number) =>
      del<DeletePaymentTerminalConnection>(
        `/payment-terminal/connection/${paymentTerminalConnectionId}`,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateOverview();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const usePostPairTerminal = (
  options?: MutationOpts<
    PostPaymentTerminalPair["response"],
    PairTerminalVariables
  >,
) => {
  return useCustomMutation({
    mutationFn: ({
      paymentTerminalConnectionId,
      ...body
    }: PairTerminalVariables) =>
      post<PostPaymentTerminalPair>(
        `/payment-terminal/connection/${paymentTerminalConnectionId}/terminal`,
        body,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateOverview();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useGetTerminal = (
  paymentTerminalId: number | null,
  options?: QueryOpts<GetPaymentTerminal["response"]>,
) => {
  return useCustomQuery({
    queryKey: [...OVERVIEW_KEY, "terminal", paymentTerminalId],
    queryFn: () =>
      get<GetPaymentTerminal>(
        `/payment-terminal/terminal/${Number(paymentTerminalId)}`,
      ),
    enabled: paymentTerminalId !== null,
    ...options,
  });
};

export const usePutTerminal = (
  options?: MutationOpts<
    PutPaymentTerminal["response"],
    UpdateTerminalVariables
  >,
) => {
  return useCustomMutation({
    mutationFn: ({ paymentTerminalId, ...body }: UpdateTerminalVariables) =>
      put<PutPaymentTerminal>(
        `/payment-terminal/terminal/${paymentTerminalId}`,
        body,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateOverview();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useDeleteTerminal = (
  options?: MutationOpts<DeletePaymentTerminal["response"], number>,
) => {
  return useCustomMutation({
    mutationFn: (paymentTerminalId: number) =>
      del<DeletePaymentTerminal>(
        `/payment-terminal/terminal/${paymentTerminalId}`,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateOverview();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const usePostTestPayment = (
  options?: MutationOpts<
    PostPaymentTerminalTestPayment["response"],
    TestPaymentVariables
  >,
) => {
  return useCustomMutation({
    mutationFn: ({ paymentTerminalId, ...body }: TestPaymentVariables) =>
      post<PostPaymentTerminalTestPayment>(
        `/payment-terminal/terminal/${paymentTerminalId}/test-payment`,
        body,
      ),
    ...options,
  });
};

export const useGetPayment = (
  terminalPaymentId: string | null,
  options?: QueryOpts<GetPaymentTerminalPayment["response"]>,
) => {
  return useCustomQuery({
    queryKey: [...OVERVIEW_KEY, "payment", terminalPaymentId],
    queryFn: () =>
      get<GetPaymentTerminalPayment>(
        `/payment-terminal/payment/${terminalPaymentId}`,
      ),
    enabled: terminalPaymentId !== null,
    ...options,
  });
};

export const usePostCancelPayment = (
  options?: MutationOpts<PostPaymentTerminalPaymentCancel["response"], string>,
) => {
  return useCustomMutation({
    mutationFn: (terminalPaymentId: string) =>
      post<PostPaymentTerminalPaymentCancel>(
        `/payment-terminal/payment/${terminalPaymentId}/cancel`,
      ),
    ...options,
  });
};

const POS_KEY = ["pos-terminals"];

export const useGetPosTerminals = (
  locationId: number | null,
  options?: QueryOpts<GetPosTerminals["response"]>,
) => {
  return useCustomQuery({
    queryKey: [...POS_KEY, locationId],
    queryFn: () =>
      get<GetPosTerminals>("/tax-register/terminal", {
        locationId: Number(locationId),
      }),
    enabled: locationId !== null,
    ...options,
  });
};

export const usePostPosTerminalPayment = (
  options?: MutationOpts<
    PostPosTerminalPayment["response"],
    { paymentTerminalId: number } & PostPosTerminalPayment["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: ({
      paymentTerminalId,
      ...body
    }: { paymentTerminalId: number } & PostPosTerminalPayment["body"]) =>
      post<PostPosTerminalPayment>(
        `/tax-register/terminal/${paymentTerminalId}/payment`,
        body,
      ),
    ...options,
  });
};

export const useGetPosTerminalPayment = (
  terminalPaymentId: string | null,
  options?: QueryOpts<GetPosTerminalPayment["response"]>,
) => {
  return useCustomQuery({
    queryKey: [...POS_KEY, "payment", terminalPaymentId],
    queryFn: () =>
      get<GetPosTerminalPayment>(
        `/tax-register/terminal/payment/${terminalPaymentId}`,
      ),
    enabled: terminalPaymentId !== null,
    ...options,
  });
};

export const usePostPosTerminalPaymentCancel = (
  options?: MutationOpts<PostPosTerminalPaymentCancel["response"], string>,
) => {
  return useCustomMutation({
    mutationFn: (terminalPaymentId: string) =>
      post<PostPosTerminalPaymentCancel>(
        `/tax-register/terminal/payment/${terminalPaymentId}/cancel`,
      ),
    ...options,
  });
};

export const usePostTapToPayDevice = (
  options?: MutationOpts<
    PostPaymentTerminalTapToPayDevice["response"],
    PostPaymentTerminalTapToPayDevice["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PostPaymentTerminalTapToPayDevice["body"]) =>
      post<PostPaymentTerminalTapToPayDevice>(
        "/payment-terminal/tap-to-pay/device",
        body,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateOverview();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const usePostTapToPaySession = (
  options?: MutationOpts<PostPaymentTerminalTapToPaySession["response"], void>,
) => {
  return useCustomMutation({
    mutationFn: () =>
      post<PostPaymentTerminalTapToPaySession>(
        "/payment-terminal/tap-to-pay/session",
      ),
    ...options,
  });
};

export const usePostTapToPayResult = (
  options?: MutationOpts<
    PostPaymentTerminalTapToPayResult["response"],
    TapToPayResultVariables
  >,
) => {
  return useCustomMutation({
    mutationFn: ({ terminalPaymentId, ...body }: TapToPayResultVariables) =>
      post<PostPaymentTerminalTapToPayResult>(
        `/payment-terminal/payment/${terminalPaymentId}/tap-to-pay-result`,
        body,
      ),
    ...options,
  });
};

export const usePostPosTapToPaySession = (
  options?: MutationOpts<
    PostPosTapToPaySession["response"],
    PostPosTapToPaySession["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PostPosTapToPaySession["body"]) =>
      post<PostPosTapToPaySession>(
        "/tax-register/terminal/tap-to-pay/session",
        body,
      ),
    ...options,
  });
};

export const usePostPosTapToPayResult = (
  options?: MutationOpts<
    PostPosTapToPayResult["response"],
    { terminalPaymentId: string } & PostPosTapToPayResult["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: ({
      terminalPaymentId,
      ...body
    }: { terminalPaymentId: string } & PostPosTapToPayResult["body"]) =>
      post<PostPosTapToPayResult>(
        `/tax-register/terminal/payment/${terminalPaymentId}/tap-to-pay-result`,
        body,
      ),
    ...options,
  });
};
